import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Trophy, Timer, Clock, MapPin, Calendar, ArrowLeft, RefreshCw,
  Play, CheckCircle, Radio, Share2, Facebook, Twitter, Link2,
  Image, Camera, ChevronLeft, ChevronRight, X, Download, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";
import LiveClock from "@/components/common/LiveClock";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};


// Share buttons component
function ShareButtons({ match, tournament }) {
  const shareUrl = window.location.href;
  const shareTitle = `${match.home_team?.name} ${match.home_score ?? 0} - ${match.away_score ?? 0} ${match.away_team?.name} | ${tournament?.name || 'Partido'}`;
  
  const handleShare = (platform) => {
    let url = '';
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareTitle)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        toast.success('Enlace copiado al portapapeles');
        return;
      default:
        return;
    }
    window.open(url, '_blank', 'width=600,height=400');
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-500 mr-2">Compartir:</span>
      <Button
        size="sm"
        variant="outline"
        className="h-9 w-9 p-0 text-blue-600 hover:bg-blue-50"
        onClick={() => handleShare('facebook')}
      >
        <Facebook className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="h-9 w-9 p-0 text-sky-500 hover:bg-sky-50"
        onClick={() => handleShare('twitter')}
      >
        <Twitter className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="h-9 w-9 p-0 text-green-600 hover:bg-green-50"
        onClick={() => handleShare('whatsapp')}
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="h-9 w-9 p-0 text-gray-600 hover:bg-gray-50"
        onClick={() => handleShare('copy')}
      >
        <Link2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

// Photo Gallery Modal
function PhotoGalleryModal({ photos, isOpen, onClose, initialIndex = 0 }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  if (!photos || photos.length === 0) return null;

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  const handleDownload = () => {
    const photo = photos[currentIndex];
    const link = document.createElement('a');
    link.href = getImageUrl(photo.url);
    link.download = `partido-foto-${currentIndex + 1}.jpg`;
    link.click();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl p-0 bg-black/95 border-0">
        <div className="relative">
          {/* Close button */}
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-4 right-4 z-10 text-white hover:bg-white/20"
            onClick={onClose}
          >
            <X className="h-6 w-6" />
          </Button>

          {/* Navigation */}
          {photos.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20 h-12 w-12"
                onClick={handlePrev}
              >
                <ChevronLeft className="h-8 w-8" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20 h-12 w-12"
                onClick={handleNext}
              >
                <ChevronRight className="h-8 w-8" />
              </Button>
            </>
          )}

          {/* Image */}
          <div className="flex items-center justify-center min-h-[60vh] p-8">
            <img
              src={getImageUrl(photos[currentIndex]?.url)}
              alt={`Foto ${currentIndex + 1}`}
              className="max-h-[70vh] max-w-full object-contain"
            />
          </div>

          {/* Footer */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            <div className="flex items-center justify-between text-white">
              <span className="text-sm">
                {currentIndex + 1} de {photos.length}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
                onClick={handleDownload}
              >
                <Download className="h-4 w-4 mr-2" />
                Descargar
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PartidoDetailPage() {
  const { matchId } = useParams();
  const [match, setMatch] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const matchRes = await axios.get(`${API}/sports/matches/${matchId}`);
      setMatch(matchRes.data);
      setLastUpdate(new Date());

      // Fetch tournament info
      if (matchRes.data.tournament_id) {
        const tournamentRes = await axios.get(`${API}/sports/tournaments/${matchRes.data.tournament_id}`);
        setTournament(tournamentRes.data);
      }
    } catch (error) {
      console.error("Error fetching match:", error);
      toast.error("Error al cargar el partido");
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds if match is live
  useEffect(() => {
    if (!autoRefresh || match?.status !== 'live') return;
    
    const interval = setInterval(() => {
      fetchData();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, match?.status, fetchData]);

  const openGallery = (index) => {
    setGalleryIndex(index);
    setGalleryOpen(true);
  };

  const getStatusBadge = () => {
    switch (match?.status) {
      case 'live':
        return (
          <Badge className="bg-red-500 text-white animate-pulse border-0 text-sm px-3 py-1">
            <Radio className="h-4 w-4 mr-1" />
            EN VIVO
          </Badge>
        );
      case 'finished':
        return (
          <Badge className="bg-gray-600 text-white border-0 text-sm px-3 py-1">
            <CheckCircle className="h-4 w-4 mr-1" />
            FINALIZADO
          </Badge>
        );
      default:
        return (
          <Badge className="bg-blue-500 text-white border-0 text-sm px-3 py-1">
            <Clock className="h-4 w-4 mr-1" />
            PROGRAMADO
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!match) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-600 mb-4">Partido no encontrado</h1>
          <Link to="/deportes">
            <Button>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver a Deportes
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const photos = match.photos || [];

  return (
    <Layout>
      {/* Match Header */}
      <section 
        className="py-8 md:py-12"
        style={{ 
          background: `linear-gradient(135deg, ${tournament?.color || '#10B981'} 0%, ${tournament?.color || '#10B981'}dd 100%)`
        }}
      >
        <div className="container mx-auto px-4">
          {/* Back button and tournament info */}
          <div className="flex items-center justify-between mb-6">
            <Link to="/deportes" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="h-5 w-5" />
              <span>Volver</span>
            </Link>
            <div className="flex items-center gap-2 text-white">
              <Trophy className="h-5 w-5" />
              <span className="font-medium">{tournament?.name}</span>
              <span className="opacity-75">• {tournament?.season}</span>
            </div>
          </div>

          {/* Main Score Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-2xl overflow-hidden"
          >
            {/* Status Bar */}
            <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-b">
              {getStatusBadge()}
              {match.status === 'live' && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={autoRefresh ? "default" : "outline"}
                    onClick={() => setAutoRefresh(!autoRefresh)}
                    className="text-xs"
                  >
                    {autoRefresh ? "Auto-actualizar ON" : "Auto-actualizar OFF"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={fetchData}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              )}
              {lastUpdate && (
                <span className="text-xs text-gray-500">
                  Actualizado: {lastUpdate.toLocaleTimeString('es-HN')}
                </span>
              )}
            </div>

            {/* Teams and Score */}
            <div className="p-6 md:p-10">
              <div className="flex items-center justify-between gap-4">
                {/* Home Team */}
                <div className="flex-1 text-center">
                  {match.home_team?.logo_url && (
                    <img
                      src={getImageUrl(match.home_team.logo_url)}
                      alt={match.home_team.name}
                      className="w-20 h-20 md:w-28 md:h-28 object-contain mx-auto mb-3"
                    />
                  )}
                  <h2 className="text-lg md:text-xl font-bold text-gray-800">
                    {match.home_team?.name}
                  </h2>
                </div>

                {/* Score */}
                <div className="flex flex-col items-center px-4 md:px-8">
                  <div className="text-5xl md:text-7xl font-black text-gray-900">
                    {match.home_score ?? 0} - {match.away_score ?? 0}
                  </div>
                  {match.status === 'live' && (
                    <div className="mt-3">
                      <LiveClock match={match} size="lg" />
                    </div>
                  )}
                  {match.status === 'finished' && (
                    <span className="mt-3 text-gray-500 font-medium">Tiempo completo</span>
                  )}
                </div>

                {/* Away Team */}
                <div className="flex-1 text-center">
                  {match.away_team?.logo_url && (
                    <img
                      src={getImageUrl(match.away_team.logo_url)}
                      alt={match.away_team.name}
                      className="w-20 h-20 md:w-28 md:h-28 object-contain mx-auto mb-3"
                    />
                  )}
                  <h2 className="text-lg md:text-xl font-bold text-gray-800">
                    {match.away_team?.name}
                  </h2>
                </div>
              </div>

              {/* Match Info */}
              <div className="flex flex-wrap items-center justify-center gap-4 mt-8 text-sm text-gray-600">
                {match.match_date && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>{new Date(match.match_date.length === 10 ? `${match.match_date}T12:00:00` : match.match_date).toLocaleDateString('es-HN', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                  </div>
                )}
                {match.match_time && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{match.match_time}</span>
                  </div>
                )}
                {match.stadium && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    <span>{match.stadium}</span>
                  </div>
                )}
              </div>

              {/* Share Buttons */}
              <div className="flex items-center justify-center mt-6 pt-6 border-t">
                <ShareButtons match={match} tournament={tournament} />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      {photos.length > 0 && (
        <section className="py-10 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Camera className="h-6 w-6 text-primary" />
                Galería del Partido ({photos.length} {photos.length === 1 ? 'foto' : 'fotos'})
              </h3>
              <ShareButtons match={match} tournament={tournament} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo, index) => (
                <motion.div
                  key={photo.media_id || index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="relative aspect-square group cursor-pointer overflow-hidden rounded-xl"
                  onClick={() => openGallery(index)}
                >
                  <img
                    src={getImageUrl(photo.url)}
                    alt={`Foto ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <Image className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* No Photos Message */}
      {photos.length === 0 && (
        <section className="py-10 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center py-12 bg-white rounded-2xl">
              <Camera className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                Aún no hay fotos de este partido
              </h3>
              <p className="text-gray-500">
                Las fotos se agregarán durante o después del partido
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Facebook Live Link */}
      {match.facebook_link && (
        <section className="py-6 bg-blue-50">
          <div className="container mx-auto px-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-white rounded-xl shadow-sm">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Facebook className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-800">Transmisión en Facebook</h4>
                  <p className="text-sm text-gray-500">Ver el partido en vivo por Facebook</p>
                </div>
              </div>
              <a
                href={match.facebook_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-blue-600 hover:bg-blue-700 gap-2">
                  <Play className="h-4 w-4" />
                  Ver Transmisión
                </Button>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Related Links */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/deportes">
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Ver todos los partidos
              </Button>
            </Link>
            {tournament && (
              <Link to={`/deportes/torneo/${tournament.tournament_id}`}>
                <Button variant="outline" className="gap-2">
                  <Trophy className="h-4 w-4" />
                  Ver {tournament.name}
                </Button>
              </Link>
            )}
            <Link to="/deportes/en-vivo">
              <Button variant="outline" className="gap-2">
                <Radio className="h-4 w-4" />
                Transmisiones en Vivo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>

      {/* Photo Gallery Modal */}
      <PhotoGalleryModal
        photos={photos}
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        initialIndex={galleryIndex}
      />
    </Layout>
  );
}
